import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import axios from 'axios';

import {
  DataBlocks,
  FormGroup,
  Button,
  Preloader,
  I18n,
  Status,
  Link,
  Heading,
  Popup,
  Tab,
  TabsList,
} from 'components';

import {
  saveUserForm,
  updateField,
  fetchUserForm,
  fetchInitialData,
} from './actions';

import styles from './styles.pcss';
import declint from '../../utils/declint';
import UpdateDataFromEsia from '../Settings/components/UpdateDataFromEsia';
import { withViewAnalytics } from '../../utils/hocs/withViewAnalytics';

const cx = classNames.bind(styles);

// TODO: move to reducer
const filterInvisibleFields = (blocks) => {
  const flatFields = blocks
    .reduce((acc, block) => [...acc, ...block.fields.reduce((fields, field) => (field.value ? [...fields, {
      id: field.id,
      value: field.value,
    }] : fields), [])], []);

  // Was added because of some dependent fields are rendered before their parents (pizdets)
  blocks.forEach((block) => {
    block.fields.sort((a, b) => a.displayOrder - b.displayOrder);
    block.fields
      .forEach((field) => {
        const self = flatFields.find((f) => f.id === field.id);

        if (self) {
          if (field.parentFieldId) {
            const parent = flatFields.find((f) => f.id === field.parentFieldId);

            if (field.linkOptionValue && parent && parent.isVisible) {
              const positiveValues = field.linkOptionValue
                .map((value) => (value[0] === '-' ? null : value))
                .filter((v) => v);
              if (~positiveValues.indexOf(String(parent.value))) {
                self.isVisible = true;
              }

              const negativeValues = field.linkOptionValue
                .map((value) => (value[0] === '-' ? value.substring(1) : null))
                .filter((v) => v);
              if (negativeValues.length) {
                if (!negativeValues.find((value) => value === parent.value)) {
                  self.isVisible = true;
                }
              }
            }
          } else {
            self.isVisible = true;
          }
        }
      });
  });

  return blocks.map((block) => ({
    ...block,
    fields: block.fields
      .map((field) => {
        if (field.parentFieldId) {
          const parent = flatFields.find((f) => f.id === field.parentFieldId);

          if (!field.linkOptionValue || !parent || !parent.isVisible) return null;

          const positiveValues = field.linkOptionValue
            .map((value) => (value[0] === '-' ? null : value))
            .filter((v) => v);
          if (~positiveValues.indexOf(String(parent.value))) {
            return field;
          }

          const negativeValues = field.linkOptionValue
            .map((value) => (value[0] === '-' ? value.substring(1) : null))
            .filter((v) => v);
          if (negativeValues.length) {
            const isNegative = Boolean(negativeValues.find((value) => value === parent.value));
            if (!isNegative) {
              return field;
            }
          }

          return null;
        }

        return field;
      })
      .filter((f) => f),
  }));
};

const getErrorFields = (error, blocks) => {
  if (!blocks) {
    return '';
  }

  const flatFields = blocks.reduce((acc, block) => [...acc, ...block.fields.reduce((fields, field) => [...fields, {
    id: field.id,
    title: field.title,
  }], [])], []);

  return blocks
    .map((errorFieldId) => {
      const field = flatFields.find(({ id }) => id === +errorFieldId);
      return field ? field.title : null;
    })
    .filter((f) => f)
    .join(', ');
};

class PersonalData extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isModalOpen: false,
      formData: null,
      activeTab: null,
      pending: true,
      isEmpty: false,
    };
  }

  componentDidMount() {
    this.props.actions.fetchInitialData(this.props.formFields);

    if (!this.props.formData) {
      axios.get('/api/profile/personaldata/')
        .then(({ data }) => {
          if (data.tabs.length > 0) {
            this.setState({
              formData: data,
              activeTab: data.tabs[0].type,
              pending: false,
            });
          } else {
            this.setState({
              isEmpty: true,
              pending: false,
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      this.setState({
        formData: this.props.formData,
        activeTab: this.props.formData.tabs[0].type,
        pending: false,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props?.error !== prevProps?.error && this.props.error?.code === 'uAPIRequestFailed') {
      this.setState({
        isModalOpen: true,
      });
    }
  }

  handlePopupClose = () => {
    this.setState({
      isModalOpen: false,
    });
  }

  validate = () => {
    const { actions, items, isValid } = this.props;

    let formValid = true;

    const visibleBlocks = filterInvisibleFields(items);

    visibleBlocks.forEach((block) => {
      block.fields.forEach(({
        value, required, id,
        regExp, minLength, maxLength, type,
      }) => {
        /**
         * флаги для валидации - нужны, чтобы засеттить ошибку при необходимости
         */
        let filled = true;
        let matchRegexp = true;
        let matchMinLength = true;
        let matchMaxLength = true;
        let errorVars = {};

        let error = '';
        // проверка на то, заполнено ли само поле
        const isValueEmpty = (!value || (typeof value === 'string' && value.trim().length === 0));

        // обязательное значение не заполено
        if (required && isValueEmpty) {
          filled = false;
          error = 'settings.userForm.errors.required';
        }

        // значение не соответствует формату
        if (filled && !isValueEmpty && (
          regExp && !RegExp(regExp).test(value)
        )) {
          matchRegexp = false;
          error = 'settings.userForm.errors.invalidStringFormat';
        }

        // меньше минимального кол-ва символов
        if (filled && type === 'input' && minLength && value
            && typeof value === 'string' && value.trim().length < minLength) {
          matchMinLength = false;
          error = 'settings.userForm.errors.invalidLength';
          errorVars = {
            comparison: 'менее',
            number: minLength,
            units: declint(minLength, ['символа', 'символов', 'символов']),
          };
        }

        // больше максимального кол-ва символов
        if (filled && type === 'input' && maxLength && matchMinLength && value
            && typeof value === 'string' && value.trim().length > maxLength) {
          matchMaxLength = false;
          error = 'settings.userForm.errors.invalidLength';
          errorVars = {
            comparison: 'более',
            number: maxLength,
            units: declint(maxLength, ['символа', 'символов', 'символов']),
          };
        }

        if (error) {
          actions.updateField({
            fieldId: id,
            value,
            error,
            blockId: block.id,
            errorVars,
          });
        }

        if (formValid && (!filled || !matchRegexp || !matchMinLength || !matchMaxLength)) {
          const el = document.getElementById(`js-field-${id}`);
          el.scrollIntoView({ alignToTop: true, behavior: 'smooth' });
          formValid = false;
        }
      });
    });

    // финальная проверка по значению из пропсов
    if (!isValid) {
      formValid = isValid;
    }

    return formValid;
  }

  render() {
    const {
      items,
      error,
      actions,
      isDirty,
      isSuccess,
      formFields,
      saveFormHandler,
      isRestrictedAccess,
    } = this.props;

    const { formData, activeTab, pending, isEmpty } = this.state;

    const checkStateOfLoading = () => {
      if (isEmpty) {
        return (
          <DataBlocks.Block className="attention">
            <I18n path="settings.userForm.errors.manualUpdate" />
          </DataBlocks.Block>
        );
      }
      if (pending) {
        return (
          <div className={cx('preloader-wrap')}>
            <Preloader size="40" className={cx('preloader')} />
          </div>
        );
      }
    };

    if (isRestrictedAccess) {
      return <Redirect to="/" />;
    }

    let visibleBlocks;
    if (items) {
      visibleBlocks = filterInvisibleFields(items);
    }

    return (
      <DataBlocks.Container>
        <DataBlocks.Block>
          {pending || isEmpty ? (
            checkStateOfLoading()
          ) : (
            <div className={cx('tab__container')}>
              {formData?.tabs?.length > 1 && (
                <TabsList
                  activeTab={activeTab}
                  className={cx('tabs')}
                  autoScroll
                  handleTabClick={(tab) => this.setState({
                    activeTab: tab,
                  })}
                >
                  {formData?.tabs?.map((tab) => (
                    <Tab
                      id={tab.type}
                      key={tab.type}
                      className={cx('tab')}
                    >
                      {tab.title}
                    </Tab>
                  ))}
                </TabsList>
              )}

              {activeTab === 'form' && visibleBlocks ? (
                <>
                  {visibleBlocks.map((block) => (
                    <FormGroup
                      {...block}
                      key={`personal-data-form-block-${block.id}`}
                      fieldIdPrefix="js-field"
                      apiDateFormat="DD.MM.YYYY"
                      fillProgress={{
                        progress: block.fields.filter((field) => field.value).length,
                        total: block.fields.length,
                      }}
                      onChange={({ fieldId, value, error: err }) => actions.updateField({
                        fieldId,
                        value,
                        error: err,
                        blockId: block.id,
                      })}
                    />
                  ))}

                  {error && (
                  <Status className={[cx('error'), 'error']} emoji="statusFail">
                    {error?.code === 'InvalidQueryParam'
                    && error?.message === 'INVALID_QUERY_PARAM'
                      && error?.errorIds
                      ? (`Следующие поля формы заполнены неверно: ${getErrorFields(error.message, error.errorIds)}`)
                      : error.message}
                  </Status>
                  )}

                  {!isSuccess && (
                    <Popup
                      onClose={this.handlePopupClose}
                      opened={this.state.isModalOpen}
                      isWide
                    >
                      <div className={cx('message')}>
                        <Heading className={cx('title')} level={1}>
                          <I18n path="common.somethingWentWrong" />
                        </Heading>
                        <Button
                          className={['big', cx('button')]}
                          onClick={this.handlePopupClose}
                        >
                          <I18n path="common.retry" />
                        </Button>
                      </div>
                    </Popup>
                  )}

                  {isSuccess ? (
                    <Status className={[cx('success'), 'success']}>
                      Данные успешно сохранены
                    </Status>
                  ) : (
                    <div className={cx('actions')}>
                      <Button
                        disabled={!isDirty}
                        onClick={() => {
                          if (this.validate()) {
                            actions.saveUserForm(saveFormHandler);
                          }
                        }}
                      >
                        <I18n path="common.save" />
                      </Button>
                      <Link
                        className={cx('revertChangesLink')}
                        onClick={() => isDirty && actions.fetchUserForm(formFields)}
                      >
                        <I18n path="common.revertChanges" />
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <UpdateDataFromEsia esiaUrl={formData?.esia?.url} />
              )}
            </div>
          )}
        </DataBlocks.Block>
      </DataBlocks.Container>
    );
  }
}

PersonalData.propTypes = {
  items: PropTypes.array,
  error: PropTypes.object,
  actions: PropTypes.object,
  isDirty: PropTypes.bool,
  isValid: PropTypes.bool,
  isSuccess: PropTypes.bool,
  formFields: PropTypes.object,
  saveFormHandler: PropTypes.func,
  isRestrictedAccess: PropTypes.bool,
  formData: PropTypes.shape({
    tabs: PropTypes.array,
    form: PropTypes.object,
    esia: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  activeTab: PropTypes.string,
};

function mapStateToProps(state) {
  return state.personalData;
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      saveUserForm,
      updateField,
      fetchUserForm,
      fetchInitialData,
    }, dispatch),
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withViewAnalytics,
)(PersonalData);
